import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Card, cn } from '@playerbase/ui';
import type {
  FeedbackForm,
  FormFieldSummary,
  FormSentimentSummary,
} from '@playerbase/types';

import { useShell } from '../../../shell/use-shell.js';
import { useDiscordGuildChannels } from '../../../games/use-discord-guild-channels.js';
import { fetchDiscordConnectionForGame } from '../../../games/discord-data.js';
import {
  fetchFeedbackForm,
  fetchFeedbackFormSubmissions,
  fetchFormFieldSummary,
  fetchFormSentimentSummary,
  FORM_RESPONSES_PAGE_SIZE,
  getFeedbackFormSubmissionsCount,
} from '../../../games/feedback-forms-data.js';
import { FormSubmissionCard } from '../../../feedback/form-submission-card.js';
import { buildActiveAt } from '../../../games/build-timeline.js';
import { FormSummaryCard } from './form-summary-card.js';
import {
  toFormSubmissionEntry,
  type FormSubmissionEntry,
} from '../../../feedback/use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Per-form responses page.
//
// /s/:studioShortId/g/:gameShortId/feedback-settings/forms/:formId/responses
//
// Read-only paginated list of every submission against one form. The
// inbox-side card is reused in expanded mode (no 200-char truncation)
// so the studio sees the full answer at a glance. Numbered pager (no
// "Show more"); each page is an offset-based slice of submissions
// ordered submitted_at desc.
// ---------------------------------------------------------------------------

const PAGE_SIZE = FORM_RESPONSES_PAGE_SIZE;

export function FormResponsesPage(): JSX.Element {
  const { studioShortId, gameShortId, formId } = useParams<{
    studioShortId: string;
    gameShortId: string;
    formId: string;
  }>();
  const { activeGame, buildMarkers, devStages } = useShell();
  const [form, setForm] = useState<FeedbackForm | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmissionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [summary, setSummary] = useState<FormFieldSummary | null>(null);
  const [sentiment, setSentiment] = useState<FormSentimentSummary | null>(null);

  // Discord-channel index, used to resolve a friendly `#name` for the
  // submission card's channel chip. Same lookup the inbox uses.
  useEffect(() => {
    if (activeGame === null) {
      setConnectionId(null);
      return;
    }
    let cancelled = false;
    void fetchDiscordConnectionForGame(activeGame.id).then((conn) => {
      if (cancelled) return;
      setConnectionId(conn?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [activeGame]);
  const { channels } = useDiscordGuildChannels(connectionId);
  const channelById = (() => {
    const map = new Map<string, { name: string }>();
    for (const c of channels) map.set(c.channelId, { name: c.name });
    return map;
  })();

  const loadInitial = useCallback(async (): Promise<void> => {
    if (formId === undefined) return;
    setLoading(true);
    setLoadError(null);
    const [loadedForm, firstPage, count, fieldSummary, sentimentSummary] =
      await Promise.all([
        fetchFeedbackForm(formId),
        fetchFeedbackFormSubmissions(formId, { offset: 0, limit: PAGE_SIZE }),
        getFeedbackFormSubmissionsCount(formId),
        fetchFormFieldSummary(formId),
        fetchFormSentimentSummary(formId),
      ]);
    if (loadedForm === null) {
      setLoadError('Form not found.');
      setLoading(false);
      return;
    }
    setForm(loadedForm);
    setSubmissions(firstPage.map(toFormSubmissionEntry));
    setTotalCount(count);
    setSummary(fieldSummary);
    setSentiment(sentimentSummary);
    setPage(0);
    setLoading(false);
  }, [formId]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  // Page change. Skips re-fetching the form metadata and the count;
  // both are loaded once on mount. The count refresh would only matter
  // if a realtime insert lands while the studio is paging, which we
  // accept as a known-stale UX and the next refresh will reconcile.
  const goToPage = useCallback(
    async (next: number): Promise<void> => {
      if (formId === undefined) return;
      if (next === page || pageLoading) return;
      setPageLoading(true);
      const fetched = await fetchFeedbackFormSubmissions(formId, {
        offset: next * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setSubmissions(fetched.map(toFormSubmissionEntry));
      setPage(next);
      setPageLoading(false);
      // Scroll the studio back to the top of the list so the new page's
      // first submission is in view.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [formId, page, pageLoading],
  );

  if (loading) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading responses...</div>;
  }
  if (loadError !== null) {
    return (
      <div className="px-2 py-6">
        <Alert tone="danger">{loadError}</Alert>
      </div>
    );
  }
  if (form === null || formId === undefined) {
    return <div className="px-2 py-6 text-sm text-text-muted">Form not found.</div>;
  }

  const builderHref =
    studioShortId === undefined || gameShortId === undefined
      ? '../'
      : `/s/${studioShortId}/g/${gameShortId}/feedback-settings/forms/${formId}`;

  // Mirrors the form-builder-page wrapper so the page chrome (chip,
  // title, action-button position) stays consistent between builder
  // and responses views.
  return (
    <div className="flex min-h-full flex-1 flex-col">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Form submissions
            </span>
            <h1 className="font-display text-2xl font-bold text-text">
              {form.name}
            </h1>
            <div className="text-xs text-text-muted">
              {totalCount === 0
                ? 'No submissions yet.'
                : `${totalCount} submission${totalCount === 1 ? '' : 's'}.`}
            </div>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to={builderHref}>&larr; Return to form</Link>
          </Button>
        </div>

        {totalCount === 0 ? (
          <Card>
            <Card.Content>
              <p className="px-2 py-4 text-sm text-text-muted">
                When players submit this form in Discord, their responses will
                appear here.
              </p>
            </Card.Content>
          </Card>
        ) : (
          <>
          {summary !== null && summary.fields.length > 0 && (
            <FormSummaryCard summary={summary} sentiment={sentiment} />
          )}
          <Card className="flex flex-col">
            <Card.Content className="p-0">
              <ul
                className={cn(
                  'divide-y divide-border',
                  pageLoading && 'opacity-60',
                )}
              >
                {submissions.map((entry) => {
                  const channel =
                    entry.surveyChannelId === null
                      ? null
                      : channelById.get(entry.surveyChannelId) ?? null;
                  return (
                    <li key={entry.id}>
                      <FormSubmissionCard
                        entry={entry}
                        channelLabel={channel?.name ?? null}
                        buildMarker={buildActiveAt(buildMarkers, devStages, entry.submittedAt)}
                        expanded
                      />
                    </li>
                  );
                })}
              </ul>
              <Pager
                page={page}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                disabled={pageLoading}
                onSelect={(next) => void goToPage(next)}
              />
            </Card.Content>
          </Card>
          </>
        )}
      </div>
    </div>
  );
}

interface PagerProps {
  page: number;
  totalCount: number;
  pageSize: number;
  disabled: boolean;
  onSelect: (next: number) => void;
}

// Compact numbered pager: ‹ 1 ... 4 5 6 ... 12 ›. Always renders the
// first + last pages and the current page +/- 1; collapses everything
// else with an ellipsis. Hidden when one page is enough to show
// everything.
function Pager({
  page,
  totalCount,
  pageSize,
  disabled,
  onSelect,
}: PagerProps): JSX.Element | null {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  if (totalPages <= 1) return null;

  const items = computePagerItems(page, totalPages);

  return (
    <nav
      aria-label="Submission pages"
      className="flex items-center justify-center gap-1 border-t border-border px-4 py-3"
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onSelect(page - 1)}
        disabled={disabled || page === 0}
        aria-label="Previous page"
      >
        &larr;
      </Button>
      {items.map((item, idx) =>
        item === 'ellipsis' ? (
          <span
            key={`ellipsis-${idx}`}
            aria-hidden
            className="px-2 text-sm text-text-muted"
          >
            …
          </span>
        ) : (
          <Button
            key={item}
            variant={item === page ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => onSelect(item)}
            disabled={disabled}
            aria-current={item === page ? 'page' : undefined}
          >
            {item + 1}
          </Button>
        ),
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onSelect(page + 1)}
        disabled={disabled || page === totalPages - 1}
        aria-label="Next page"
      >
        &rarr;
      </Button>
    </nav>
  );
}

function computePagerItems(
  page: number,
  totalPages: number,
): Array<number | 'ellipsis'> {
  const indices = new Set<number>([0, totalPages - 1, page, page - 1, page + 1]);
  const visible = [...indices]
    .filter((n) => n >= 0 && n < totalPages)
    .sort((a, b) => a - b);
  const out: Array<number | 'ellipsis'> = [];
  for (let i = 0; i < visible.length; i += 1) {
    const n = visible[i]!;
    if (i > 0 && n - visible[i - 1]! > 1) out.push('ellipsis');
    out.push(n);
  }
  return out;
}
